import { useEffect } from 'react'
import { Outlet } from 'react-router'
import { useAuthStore } from '@/store/auth-store'
import { useRealtime } from '@/shared/lib/use-realtime'
import { listenForForegroundPush } from '@/shared/lib/push'
import { useNotificationStore } from '@/store/notification-store'
import { AuthGuard } from '@/layouts/auth-guard'
import { MobileNav } from '@/shared/components/navigation/mobile-nav'
import { BrandHeader } from '@/shared/components/navigation/brand-header'

export function ProtectedLayout() {
  return (
    <AuthGuard>
      <ProtectedShell />
    </AuthGuard>
  )
}

function ProtectedShell() {
  const profile = useAuthStore((s) => s.profile)
  const refreshProfile = useAuthStore((s) => s.refreshProfile)
  const { load, subscribe } = useNotificationStore()

  useEffect(() => {
    if (!profile) return
    load(profile.id)
    return subscribe(profile.id)
  }, [profile, load, subscribe])

  // A push that arrives while the tab is focused never reaches the service worker, so it
  // would otherwise be dropped. Real notifications also insert a row that the realtime
  // subscription above picks up, but a message sent directly to the token (for example a
  // Firebase console test) has no row, so reload explicitly.
  useEffect(() => {
    if (!profile) return
    let unsubscribe: (() => void) | undefined
    void listenForForegroundPush(() => {
      void load(profile.id)
    }).then((fn) => {
      unsubscribe = fn
    })
    return () => unsubscribe?.()
  }, [profile, load])

  // Verification outcomes and account state land on the connector row and its bank
  // account. Refreshing the store here updates every screen that reads the profile,
  // including the dashboard KYC block and the profile page.
  useRealtime(
    `account:${profile?.id}`,
    profile
      ? [
          { table: 'connectors', filter: `id=eq.${profile.id}` },
          { table: 'connector_bank_accounts', filter: `connector_id=eq.${profile.id}` },
        ]
      : [],
    () => {
      void refreshProfile()
    },
    !!profile
  )

  return (
    <div className="min-h-screen bg-canvas">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-700 focus:shadow-raised"
      >
        Skip to main content
      </a>
      <BrandHeader />
      <main
        id="main-content"
        className="mx-auto w-full max-w-[1800px] overflow-x-clip px-4 pb-28 pt-5 md:px-6 md:pt-6 lg:px-8 lg:pb-14 2xl:px-10"
      >
        <Outlet />
      </main>
      <MobileNav />
    </div>
  )
}
